import { createContext, useContext, useMemo, useRef, useState, type ReactNode } from 'react'

/**
 * Shared "first panel" claim used by every `<ConfigPanel>` rendered
 * inside a `<ConfigPanelGroup>`. The first panel to call `claimFirst`
 * during a render pass becomes the group's primary section: it
 * defaults to expanded AND gets the `accent` border so the user has a
 * clear entry point when they land on the screen.
 *
 * Persisted localStorage state still wins — the group rule only
 * applies when nothing has been persisted for that panel.
 *
 * `singleExpand` mode adds accordion behaviour on top: opening a panel
 * auto-collapses any sibling that was open. The group owns the open id
 * (via `openId`/`setOpenId`); panels read it to compute their expanded
 * state. localStorage is ignored in this mode — accordion state is
 * always implicit-from-group, never persisted.
 */
interface GroupCtx {
  claimFirst: (id: string) => boolean
  /** True when the parent group enforces accordion (one open at a time). */
  singleExpand: boolean
  /** Currently-open panel id when `singleExpand` is true; otherwise null. */
  openId: string | null
  /** Set the open panel; `null` collapses all. No-op when `singleExpand` is false. */
  setOpenId: (id: string | null) => void
}

const ConfigPanelGroupCtx = createContext<GroupCtx | null>(null)

export function useConfigPanelGroupFirst(id: string): boolean {
  const ctx = useContext(ConfigPanelGroupCtx)
  return ctx ? ctx.claimFirst(id) : false
}

/**
 * Read the accordion state for a panel. Returns `null` when there's no
 * group, or when the group isn't in `singleExpand` mode — caller should
 * fall back to its own state in that case.
 */
export function useConfigPanelGroupAccordion(id: string): {
  accordion: boolean
  isOpen: boolean
  open: () => void
  close: () => void
} | null {
  const ctx = useContext(ConfigPanelGroupCtx)
  if (!ctx || !ctx.singleExpand) return null
  return {
    accordion: true,
    isOpen: ctx.openId === id,
    open: () => ctx.setOpenId(id),
    close: () => ctx.setOpenId(null),
  }
}

export interface ConfigPanelGroupProps {
  children: ReactNode
  /**
   * When true, only one panel in the group can be expanded at a time.
   * Opening a panel auto-collapses the previously-open sibling.
   * Defaults to false — existing groups stay independent.
   */
  singleExpand?: boolean
  /**
   * Initial open id when `singleExpand` is true. `null` (default) means
   * everything starts collapsed unless a child has `defaultExpanded` /
   * is the group-first or has `expandWhen` true at mount.
   */
  defaultOpenId?: string | null
}

/**
 * Wrap a stack of `<ConfigPanel>`s to mark the first as the primary
 * section (expanded by default + accent styling). Subsequent panels
 * keep their default collapsed-and-neutral appearance.
 *
 * The group adds **no DOM** — it's a context provider only, so the
 * caller's existing flex/grid container drives layout.
 *
 * ```tsx
 * <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--gap)' }}>
 *   <ConfigPanelGroup>
 *     <PricingDefaultsPanel />
 *     <PricingUpdatePolicyPanel />
 *   </ConfigPanelGroup>
 * </div>
 * ```
 *
 * Conditional rendering and wrapper components are supported because
 * the claim runs inside each `<ConfigPanel>` itself, not via cloning.
 */
export function ConfigPanelGroup({
  children,
  singleExpand = false,
  defaultOpenId = null,
}: ConfigPanelGroupProps) {
  // First-id-wins claim, scoped to a single render pass. Resetting on
  // every render keeps the rule stable when the same children render
  // in the same order — and self-corrects if the tree changes.
  const firstIdRef = useRef<string | null>(null)
  firstIdRef.current = null

  const [openId, setOpenId] = useState<string | null>(defaultOpenId)

  const ctx = useMemo<GroupCtx>(
    () => ({
      claimFirst(id: string) {
        if (firstIdRef.current === null) {
          firstIdRef.current = id
        }
        return firstIdRef.current === id
      },
      singleExpand,
      openId,
      setOpenId,
    }),
    [singleExpand, openId],
  )

  return (
    <ConfigPanelGroupCtx.Provider value={ctx}>
      {children}
    </ConfigPanelGroupCtx.Provider>
  )
}
